import type { FunctionComponent } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { buttonize } from '../../../../utils/a11y.utils';

import { IconChevronLeftNarrow } from '../../../../common/icons/icon-chevron-left-narrow';
import { IconChevronRightNarrow } from '../../../../common/icons/icon-chevron-right-narrow';
import { useTranslation } from '../../../../i18n';
import type { Video } from '../../../models/video.interface';
import { VideoBarItem } from '../video-bar-item/video-bar-item.component';

export interface Props {
  videoBar: Video[];
  columnCount: number;
  showBadge: boolean;
  showVideoTitle: boolean;
  showVideoDate: boolean;
  showVideoArtist: boolean;
  showCardDropShadow: boolean;
  badgeText: string;
  displayType: 'scroll' | 'grid';
}

export const VideoBarSlider: FunctionComponent<Props> = ({
  videoBar,
  columnCount,
  showBadge,
  showVideoTitle,
  showVideoDate,
  showVideoArtist,
  showCardDropShadow,
  badgeText,
  displayType,
}) => {
  const { t } = useTranslation();
  const [sliderInitialized, setSliderInitialized] = useState(false);
  const [canSlideLeft, setCanSlideLeft] = useState(false);
  const [canSlideRight, setCanSlideRight] = useState(false);
  const [videoWidth, setVideoWidth] = useState(350);
  const [thumbnailHeight, setThumbnailHeight] = useState('197px');
  const [visibleVideoCount, setVisibleVideoCount] = useState(4.5);

  const videoSliderTrackRef = useRef<HTMLDivElement | null>(null);

  const calculateResponsiveColumnCount = () => {
    const windowWidth = window.innerWidth;

    if (windowWidth <= 768) {
      setVisibleVideoCount(1.5);
    } else if (windowWidth <= 990) {
      setVisibleVideoCount(2.5);
    } else {
      setVisibleVideoCount(columnCount ? Number(columnCount) + 0.5 : 4.5);
    }
  };

  const calculateVideoWidth = () => {
    if (videoSliderTrackRef.current) {
      const containerWidth = videoSliderTrackRef.current.offsetWidth;

      if (containerWidth < 100) {
        console.warn('Container width not ready, retrying...');
        setTimeout(() => calculateVideoWidth(), 50);
        return;
      }

      const totalMargin = (visibleVideoCount - 1) * 16;
      const width = (containerWidth - totalMargin) / visibleVideoCount;
      setVideoWidth(width);

      const height = (width * 9) / 16;
      setThumbnailHeight(`${height}px`);
    } else {
      console.warn('Video slider track element not found, using fixed width');
      setVideoWidth(350);
      setThumbnailHeight('197px');
    }
  };

  const updateNavigationState = () => {
    if (videoSliderTrackRef.current) {
      const container = videoSliderTrackRef.current;
      setCanSlideLeft(container.scrollLeft > 1);

      const scrollThreshold = 2;
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      setCanSlideRight(container.scrollLeft < maxScrollLeft - scrollThreshold);
    }
  };

  const waitForSliderElement = (attempts = 0) => {
    if (videoSliderTrackRef.current) {
      calculateVideoWidth();
      setSliderInitialized(true);
      setTimeout(() => {
        updateNavigationState();
      }, 100);
    } else if (attempts < 20) {
      setTimeout(() => waitForSliderElement(attempts + 1), 50);
    } else {
      console.warn('Video slider element not found after 1 second, using fallback');
      setVideoWidth(350);
      setThumbnailHeight('197px');
      setSliderInitialized(true);
    }
  };

  const initializeSlider = () => {
    if (videoBar && videoBar.length > 0) {
      if (displayType === 'scroll') {
        calculateResponsiveColumnCount();
        waitForSliderElement();
      } else {
        setSliderInitialized(true);
      }
    }
  };

  const slideNext = () => {
    if (canSlideRight && videoSliderTrackRef.current) {
      const firstVideoItem = videoSliderTrackRef.current.querySelector('.single-video-slider-item');
      const computedStyle = firstVideoItem ? window.getComputedStyle(firstVideoItem) : null;
      const actualMarginRight = computedStyle ? parseFloat(computedStyle.marginRight) : 16;

      const videosToSlide = Math.floor(visibleVideoCount);
      const scrollDistance = videosToSlide * (videoWidth + actualMarginRight);

      videoSliderTrackRef.current.scrollBy({
        left: scrollDistance,
        behavior: 'smooth',
      });
    }
  };

  const slidePrev = () => {
    if (canSlideLeft && videoSliderTrackRef.current) {
      const firstVideoItem = videoSliderTrackRef.current.querySelector('.single-video-slider-item');
      const computedStyle = firstVideoItem ? window.getComputedStyle(firstVideoItem) : null;
      const actualMarginRight = computedStyle ? parseFloat(computedStyle.marginRight) : 16;

      const videosToSlide = Math.floor(visibleVideoCount);
      const videoTotalWidth = videoWidth + actualMarginRight;
      const currentScrollLeft = videoSliderTrackRef.current.scrollLeft;

      const currentVideoIndex = Math.round(currentScrollLeft / videoTotalWidth);
      const targetVideoIndex = Math.max(0, currentVideoIndex - videosToSlide);
      const targetScrollLeft = targetVideoIndex * videoTotalWidth;

      videoSliderTrackRef.current.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const onScroll = () => {
    updateNavigationState();
  };

  const onWindowResize = useCallback(() => {
    if (displayType === 'scroll' && videoBar && videoBar.length > 0) {
      calculateResponsiveColumnCount();
      calculateVideoWidth();
      updateNavigationState();
    }
  }, [displayType, videoBar, columnCount, visibleVideoCount]);

  // Initialize on mount
  useEffect(() => {
    initializeSlider();
  }, [videoBar, displayType]);

  // Handle window resize
  useEffect(() => {
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, [onWindowResize]);

  // Re-calculate when visibleVideoCount changes
  useEffect(() => {
    if (sliderInitialized) {
      calculateVideoWidth();
      updateNavigationState();
    }
  }, [visibleVideoCount]);

  if (displayType !== 'scroll') {
    return null;
  }

  if (!videoBar || videoBar.length === 0) {
    return (
      <div className="single-video-no-results">
        <h2>{t('perks.noVideos')}</h2>
      </div>
    );
  }

  return (
    <div className="single-card-row single-video-slider">
      <div
        className={`single-chevron left ${sliderInitialized && canSlideLeft ? 'visible' : ''}`}
        {...buttonize(slidePrev, t('a11y.previousSlide'))}
      >
        <IconChevronLeftNarrow fill="#fff" />
      </div>

      <div className="single-video-slider-container" ref={videoSliderTrackRef} onScroll={onScroll}>
        <div className="single-video-slider-track">
          {sliderInitialized &&
            videoBar.map((video, index) => (
              <div
                key={video.id || index}
                className="single-video-slider-item"
                style={{ width: `${videoWidth}px` }}
              >
                <VideoBarItem
                  video={video}
                  showTitle={showVideoTitle}
                  showDate={showVideoDate}
                  showArtist={showVideoArtist}
                  showBadge={showBadge}
                  showCardDropShadow={showCardDropShadow}
                  badgeText={badgeText}
                />
              </div>
            ))}
        </div>
      </div>

      <div
        className={`single-chevron right ${sliderInitialized && canSlideRight ? 'visible' : ''}`}
        {...buttonize(slideNext, t('a11y.nextSlide'))}
      >
        <IconChevronRightNarrow fill="#fff" />
      </div>
    </div>
  );
};
